import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCatalog } from '../../contexts/CatalogContext'
import { useSearchTracking } from '../../hooks/useSearchTracking'
import { skinTypes } from '../../data/concerns'
import type { RoutineStep, SkinType } from '../../core/types/catalog'
import { filtersFromParams, PAGE_SIZE, paramsFromFilters, visibleProducts, type Sort } from './shopRules'

export type ActiveChip = { label: string; onRemove: () => void }

/**
 * De filters van de webshop. Alles staat in de URL (?huid=&merk=&stap=&sorteer=&zoek=):
 * deelbaar, en "terug" vanaf een product brengt je in dezelfde selectie.
 */
export function useShopFilters() {
  const { products, brands } = useCatalog()
  const [params, setParams] = useSearchParams()
  const [initial] = useState(() => filtersFromParams(params, { skins: skinTypes, brands: brands.map((b) => b.name), products }))

  const [skin, setSkin] = useState<SkinType | null>(initial.skin)
  const [brand, setBrand] = useState<string | null>(initial.brand)
  const [step, setStep] = useState<RoutineStep | null>(initial.step)
  const [sort, setSort] = useState<Sort>(initial.sort)
  const [query, setQuery] = useState(initial.query)
  const [limit, setLimit] = useState(PAGE_SIZE)

  // Deeplink vanuit klachtenkiezer, productpagina of artikel terwijl je al in de shop bent: /webshop?huid=Pigmentatie
  const linkedSkin = skinTypes.find((s) => s === params.get('huid')) ?? null
  useEffect(() => {
    if (linkedSkin) setSkin(linkedSkin)
  }, [linkedSkin])

  // Nieuwe filter of sortering: begin weer bovenaan de selectie.
  useEffect(() => setLimit(PAGE_SIZE), [skin, brand, step, sort, query])

  // Filters → URL. `replace`, zodat niet elke toetsaanslag een stap in de geschiedenis wordt.
  useEffect(() => {
    const next = paramsFromFilters({ skin, brand, step, sort, query })
    if (next.toString() !== params.toString()) setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- params hoort er niet bij: dat zou een lus geven
  }, [skin, brand, step, sort, query])

  const visible = useMemo(() => visibleProducts(products, { skin, brand, step, sort, query }), [products, skin, brand, step, sort, query])
  // Anoniem: wat er gezocht wordt en of het iets opleverde (Inzicht › Bereik › Zoeken op de site).
  useSearchTracking(query, visible.length)

  const extraFilterCount = (step ? 1 : 0) + (brand ? 1 : 0)
  const hasFilters = skin !== null || extraFilterCount > 0 || query !== ''
  const clear = () => {
    setSkin(null)
    setBrand(null)
    setStep(null)
    setQuery('')
  }

  const activeChips: ActiveChip[] = []
  if (skin) activeChips.push({ label: skin, onRemove: () => setSkin(null) })
  if (step) activeChips.push({ label: step, onRemove: () => setStep(null) })
  if (brand) activeChips.push({ label: brand, onRemove: () => setBrand(null) })

  return {
    skin,
    setSkin,
    brand,
    setBrand,
    step,
    setStep,
    sort,
    setSort,
    query,
    setQuery,
    visible,
    shown: visible.slice(0, limit),
    hasMore: visible.length > limit,
    limit,
    showMore: () => setLimit((l) => l + PAGE_SIZE),
    extraFilterCount,
    hasFilters,
    clear,
    activeChips,
  }
}

export type ShopFilterControl = ReturnType<typeof useShopFilters>
